import { useLayoutEffect, useRef } from 'react';
import { useAtomValue } from 'jotai';
import { type ScrollAlignT, SCROLL_ALIGN } from '../../../common/virtual-grid';
import { currentFrameAtom } from '../atoms/video-playback-atoms';
import { getAutoScrollTarget } from '../utils/frame-timeline-v4';

/**
 * Keeps the current frame in view by scrolling a buffer of lead-in frames ahead of it
 * (in the direction of travel), so the playhead never sits flush against an edge.
 *
 * Runs in a layout effect (not a passive effect) so the programmatic scroll lands in the
 * same frame as the marker's transform update. A passive effect would scroll *after* the
 * paint that already moved the marker, so the marker would jump ahead of the pointer and
 * then snap back once the scroll caught up — visible flicker, especially under CPU throttle.
 */
export function useFrameAutoScroll(scrollToColumn: (index: number, align?: ScrollAlignT) => void, totalFrames: number) {
  const currentFrame = useAtomValue(currentFrameAtom);
  const prevFrameRef = useRef(currentFrame);
  useLayoutEffect(() => {
    const direction = currentFrame >= prevFrameRef.current ? 1 : -1;
    prevFrameRef.current = currentFrame;
    scrollToColumn(getAutoScrollTarget(currentFrame, direction, totalFrames), SCROLL_ALIGN.auto);
  }, [currentFrame, totalFrames, scrollToColumn]);
}